import { FormEventHandler, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import { Button } from '@bm-js/atlascloud-ui'
import LinkButton from '../components/LinkButton'
import Input from '../components/Input'
import Loader from '../components/Loader'
import Logo from '../components/Logo'
import { breakPointsCss, colors, measurments } from '../utils/config'
import { fetcher } from '../utils/fetcher'
import { useQuery } from '../utils/useQuery'
import { ChangeEvent, UserBM } from '@bm-js/atlascloud-shared'
import { useAuthDispatch } from '../utils/hooks'
import { AuthActionType } from '../reducers/AuthState.reducer'

const StyledRegister = styled.div`
  .grid {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100vh;
    .form-container {
      display: flex;
      align-items: flex-start;
      gap: 1rem;
    }
    form,
    .requirements {
      background: white;
      padding: 2.2rem 2.5rem;
      border-radius: ${measurments.borderRadiusLarge}px;
      box-shadow: 0px 32px 64px -12px rgba(16, 24, 40, 0.2);
      min-width: 40ch;
    }
  }
  ul {
    padding-left: 1rem;
  }
  .loader-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
  }
  ${breakPointsCss.tablet} {
    .grid {
      .form-container {
        display: block;
        form {
          margin-bottom: 1rem;
        }
      }
    }
  }
`

const Register = () => {
  //Static
  const initialUser = {
    password: '',
    repeatPassword: '',
  }

  //Global
  const navigate = useNavigate()
  const token = useQuery().get('t')
  const authDispatch = useAuthDispatch()

  //States
  const [formData, setFormData] = useState(initialUser)
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [userFromToken, setUserFromToken] = useState<UserBM>()

  //Functions
  const handleChange = (e: ChangeEvent) => {
    setErrorMessage('')
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const loginUser = async () => {
    setErrorMessage('')
    const { err, data } = await fetcher<{ user: UserBM; token: string }>({
      path: 'customer-no-auth/login',
      method: 'POST',
      body: { email: userFromToken!.email, password: formData.password },
    })
    if (err || !data) {
      navigate('/')
      return
    }
    authDispatch({
      type: AuthActionType.mergeState,
      data: { token: data.token },
    })
  }

  const handleSubmit: FormEventHandler = async (e) => {
    e.preventDefault()
    if (formData.password !== formData.repeatPassword) {
      setErrorMessage('Lösenorden stämmer inte överens')
      return
    }
    setLoading(true)
    const { err } = await fetcher({
      path: 'customer-no-auth/create-user-password',
      auth: token,
      method: 'POST',
      body: { password: formData.password },
    })
    if (err) {
      setErrorMessage('Lösenordet uppfyller inte kraven')
      setLoading(false)
      setFormData(initialUser)
      return
    }
    loginUser()
  }

  const verifyToken = async () => {
    const { err, data } = await fetcher<UserBM>({
      path: 'customer-no-auth/verify-register-token',
      auth: token,
    })
    if (err || !data) {
      setLoading(false)
      return
    }
    setUserFromToken(data)
    setLoading(false)
  }

  useEffect(() => {
    //eslint-disable-next-line
    verifyToken()
    //eslint-disable-next-line
  }, [token])

  if (!userFromToken && !loading)
    return (
      <StyledRegister className='content'>
        <div className='loader-container fdc c flex'>
          <h1 className='m0 primblue l'>Felaktig länk</h1>
          <p>
            Länken är felaktig eller för gammal. Kontakta din administratör för
            att be om en ny länk.
          </p>
          <a href='https://atlascloud.se'>
            <LinkButton>Tillbaka till atlascloud.se</LinkButton>
          </a>
        </div>
      </StyledRegister>
    )

  if (!userFromToken && loading)
    return (
      <StyledRegister>
        <div className='loader-container fdc flex'>
          <p className='primblue s'>Verifierar länk...</p>
          <Loader color={colors.primary400} />
        </div>
      </StyledRegister>
    )

  return (
    <StyledRegister className='content'>
      <div className='jcsb ac ptop1 pleft1 flex'>
        <Logo />
      </div>
      <div className='grid'>
        <div className='form-container'>
          <form onSubmit={handleSubmit}>
            <p className='m0 mbottom1 b m primblue'>
              Välkommen {userFromToken?.fullName}!
            </p>
            <Input
              label='Lösenord'
              desc='Välj ett starkt lösenord'
              required
              disabled={loading}
              type='password'
              value={formData.password}
              name='password'
              onChange={handleChange}
            />
            <Input
              label='Upprepa lösenord'
              desc='Upprepa lösenordet du angivit'
              required
              disabled={loading}
              type='password'
              value={formData.repeatPassword}
              name='repeatPassword'
              onChange={handleChange}
            />
            <div className='jce flex'>
              <Button type='submit' disabled={loading}>
                {loading ? 'Skapar konto...' : 'Skapa konto'}
              </Button>
            </div>
            {errorMessage && <p className='m0 mtop1 error'>{errorMessage}</p>}
          </form>
          <div className='requirements'>
            <p className='m0 primblue b'>
              Lösenordet måste uppfylla följande krav:
            </p>
            <ul>
              <li>Minst 8 tecken</li>
              <li>Minst en liten bokstav</li>
              <li>Minst en stor bokstav</li>
              <li>Minst en siffra</li>
            </ul>
          </div>
        </div>
      </div>
    </StyledRegister>
  )
}

export default Register
